import React, { useEffect, useState, useCallback } from "react";
import { Star, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Skeleton, ErrorState } from "../ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

function ratingColor(r) {
  if (r >= 4.5) return "#00ff90";
  if (r >= 3.5) return "#60a5fa";
  if (r >= 2.5) return "#fbbf24";
  return "#f87171";
}

function TrendIcon({ trend }) {
  if (trend === "up")   return <TrendingUp  size={14} className="text-[#00ff90]" />;
  if (trend === "down") return <TrendingDown size={14} className="text-red-400"   />;
  return <Minus size={14} className="text-white/30" />;
}

export default function PerformanceSummary() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/api/performance/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setSummary(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance Summary</span>
        </div>
        <div className="p-5" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Skeleton width={64} height={64} radius={12} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <Skeleton width="50%" height={12} />
              <Skeleton width="35%" height={10} />
            </div>
          </div>
          <Skeleton width="100%" height={50} radius={8} />
          <Skeleton width="100%" height={50} radius={8} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={card}>
        <div className="p-5">
          <ErrorState message="Couldn't load performance summary" detail={error} onRetry={load} />
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const rating = typeof summary.rating === "number" ? summary.rating : parseFloat(summary.rating);
  const color  = ratingColor(rating);

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance Summary</span>
        {summary.period && <span className="text-xs text-white/30 border border-white/10 px-2 py-0.5 rounded-full">{summary.period}</span>}
      </div>

      <div className="p-5 space-y-4">
        {/* Rating */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl border flex items-center justify-center shrink-0"
            style={{ borderColor: `${color}40`, background: `${color}10` }}>
            <span className="text-2xl font-bold" style={{ color }}>{isNaN(rating) ? "—" : rating.toFixed(1)}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Star size={14} style={{ color }} fill={color} />
              <span className="text-sm text-white/60">Overall Rating</span>
              {summary.trend && <TrendIcon trend={summary.trend} />}
            </div>
            {summary.cycle && <p className="text-xs text-white/30 mt-0.5">{summary.cycle}</p>}
          </div>
        </div>

        {/* Strengths */}
        {summary.strengths && (
          <div className="rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 px-4 py-3">
            <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-1">Strengths</p>
            <p className="text-sm text-white/70">{summary.strengths}</p>
          </div>
        )}

        {/* Areas for improvement */}
        {summary.improvement && (
          <div className="rounded-lg border border-yellow-500/15 bg-yellow-500/5 px-4 py-3">
            <p className="text-xs text-yellow-400/50 uppercase tracking-wider mb-1">Areas for Improvement</p>
            <p className="text-sm text-white/70">{summary.improvement}</p>
          </div>
        )}

        {/* Manager comment */}
        {summary.manager_comment && (
          <div className="rounded-lg border border-white/5 bg-white/3 px-4 py-3">
            <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Manager Comment</p>
            <p className="text-sm text-white/60 italic">"{summary.manager_comment}"</p>
          </div>
        )}
      </div>
    </div>
  );
}